/**
 * The plan, saved: what the PDF and the calendar file say (#39 US-5.3, #50 US-10.1).
 *
 * **Built on the visitor's device out of the plan card, with no model and no
 * network.** The card is the graph's snapshot (`TripPlan`), so what the files say
 * is what the card under the answer says — the same stops in the same order, the
 * same numbers (never renumbered, `PlanTimeline`), the same hours, closures and
 * ticket links — plus the three fields the graph writes for the export only
 * (`peterhof_plan.plan.to_card`): the ISO `dates`, `as_of` and `next_visit`.
 *
 * **No date, no calendar entry.** An event is put only on a day in `dates`, and
 * the graph writes `dates` only when they ARE the day the card's `date` line says.
 * A plan with no date gets no `.ics` at all (`planIcs` → `null`) rather than one
 * on today or on a day parsed out of «в выходные».
 *
 * **The times are the card's, as text.** `hours` is a display string («10:30–
 * 11:10», «с 11:00», an opening-hours line on another graph), so it is printed
 * beside the stop and never parsed into a timed event: an all-day event with the
 * hours in its text is right whatever the string means; a 10:30 event is right
 * only if we guessed which of those it was.
 *
 * Pure: no DOM, no jsPDF — `planPdf.ts` draws `planDocument`, `download.ts` saves.
 */

import type { TripPlanProps } from '../catalog/generated/schemas';
import { stripEmoji as plain } from '../lib/emoji';

type PlanDay = TripPlanProps['days'][number];
type PlanItem = NonNullable<PlanDay['items']>[number];

/** The card's own badge words (`PlanTimeline`), one table for both. */
export const STATUS_LABEL: Record<string, string> = {
  closed: 'закрыто',
  dayoff: 'выходной',
  sanitary: 'санитарный день',
};

/** An unnumbered row's word where the card draws a glyph. */
export const KIND_WORD: Record<PlanItem['kind'], string> = {
  arrival: 'Прибытие',
  entrance: 'Вход',
  break: 'Перерыв',
  stop: '•',
};

/** Russian needs three forms, and «8 точка» is what a visitor notices first. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/** «≈ 2,5 ч» — the WHOLE visit, at the points and between them (INV-PH-113); `hours` is the graph's. */
export function visitHours(hours: number): string {
  return `≈ ${String(hours).replace('.', ',')} ч`;
}

/** «6 точек · ≈ 2,5 ч (на точках 1 ч 35 мин · переходы ≈ 55 мин)» — the card's totals line, the same
 *  words in the files. The words in the brackets are the graph's (`totals.breakdown`), printed as given:
 *  the client has neither the coordinates nor the file's numbers to recompute them. */
export function planTotals(props: TripPlanProps): string {
  const totals: string[] = [];
  if (props.totals) {
    const { stops, hours, breakdown } = props.totals;
    if (stops > 0) totals.push(`${stops} ${plural(stops, 'точка', 'точки', 'точек')}`);
    if (typeof hours === 'number' && hours > 0) {
      totals.push(breakdown ? `${visitHours(hours)} (${breakdown})` : visitHours(hours));
    } else if (breakdown) {
      totals.push(breakdown);
    }
  }
  return totals.join(' · ');
}

export const NO_DATE = 'Дата визита не выбрана';
export const CHECK_LINE =
  'Часы работы, цены и закрытия могут измениться — перед поездкой проверьте на peterhofmuseum.ru.';

export type ExportStop = {
  /** `n` for a numbered stop, else the row's kind in words. */
  marker: string;
  title: string;
  hours?: string;
  status?: string;
  subtitle?: string;
  note?: string;
  walk?: string;
  ticketUrl?: string;
};

export type ExportDay = { heading?: string; stops: ExportStop[] };

export type NextVisitItem = { title: string; summary?: string; season?: string };

export type PlanDocument = {
  title: string;
  date: string;
  totals: string;
  days: ExportDay[];
  nextVisit: NextVisitItem[];
  /** «Данные на 07.10.2026» — the day the card's facts were read; '' when the card does not say. */
  asOf: string;
  /** «Сохранено 08.10.2026» — when this file was made. */
  savedAt: string;
  /** The way back into the guide (`returnLink.planLink`, INV-PH-166) — the PDF prints it with a QR code; `null` when the card has none. */
  link: string | null;
};

/** The line before the link, in the PDF and in the calendar's text. */
export const LINK_LINE = 'Ваш личный гид — вернуться к этому плану';

/** `2026-10-07` → `07.10.2026`; anything else as it came. */
export function ruDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function localIso(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The days the card draws, in its order: the empty ones left out, as on the card. */
function cardDays(props: TripPlanProps): PlanDay[] {
  return props.days.filter((day) => (day.items ?? []).length > 0);
}

function exportStop(item: PlanItem): ExportStop {
  const stop: ExportStop = {
    marker: typeof item.n === 'number' ? String(item.n) : KIND_WORD[item.kind],
    title: plain(item.title),
  };
  if (item.hours) stop.hours = plain(item.hours);
  const status = item.status ? STATUS_LABEL[item.status] : undefined;
  if (status) stop.status = status;
  if (item.subtitle) stop.subtitle = plain(item.subtitle);
  if (item.note) stop.note = plain(item.note);
  if (typeof item.from_prev_m === 'number') stop.walk = `~${item.from_prev_m} м от предыдущей точки`;
  if (item.tickets_url) stop.ticketUrl = item.tickets_url;
  return stop;
}

export function planDocument(props: TripPlanProps, now: Date, link: string | null = null): PlanDocument {
  const days = cardDays(props);
  const multi = days.length > 1;
  return {
    title: plain(props.title ?? 'План поездки'),
    date: plain(props.date ?? NO_DATE),
    totals: planTotals(props),
    days: days.map((day, index) => ({
      ...(multi ? { heading: plain(day.label ?? day.date ?? `День ${index + 1}`) } : {}),
      stops: (day.items ?? []).map(exportStop),
    })),
    nextVisit: (props.next_visit ?? []).map((item) => ({
      title: plain(item.title),
      ...(item.summary ? { summary: plain(item.summary) } : {}),
      ...(item.season ? { season: plain(item.season) } : {}),
    })),
    asOf: props.as_of ? `Данные на ${ruDate(props.as_of)}` : '',
    savedAt: `Сохранено ${ruDate(localIso(now))}`,
    link,
  };
}

/** One stop as one line of plain text — the calendar's description. */
export function stopLine(stop: ExportStop): string {
  const head = /^\d+$/.test(stop.marker) ? `${stop.marker}. ${stop.title}` : `${stop.marker}: ${stop.title}`;
  const parts = [head];
  if (stop.hours) parts.push(`время: ${stop.hours}`);
  if (stop.status) parts.push(stop.status);
  if (stop.subtitle) parts.push(stop.subtitle);
  let line = parts.join(' — ');
  if (stop.note) line += `. ${stop.note.charAt(0).toUpperCase()}${stop.note.slice(1)}`;
  if (stop.walk) line += ` (${stop.walk})`;
  if (stop.ticketUrl) line += `\nБилет: ${stop.ticketUrl}`;
  return line;
}

/** The file name, dated by the visit when there is one. */
export function exportFileName(props: TripPlanProps, ext: 'pdf' | 'ics'): string {
  const first = props.dates?.[0];
  return first ? `peterhof-plan-${first}.${ext}` : `peterhof-plan.${ext}`;
}

// ---------------------------------------------------------------------------
// the calendar (RFC 5545)
// ---------------------------------------------------------------------------

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** TEXT escaping, RFC 5545 §3.3.11. */
function icsText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Content lines are folded at 75 OCTETS (§3.1) — and Cyrillic is two each in
 *  UTF-8, so counting characters would emit 140-byte lines. Never inside a
 *  code point. */
export function foldLine(line: string): string {
  const encoder = new TextEncoder();
  const out: string[] = [];
  let current = '';
  let bytes = 0;
  for (const ch of line) {
    const size = encoder.encode(ch).length;
    const limit = out.length === 0 ? 75 : 74; // a continuation starts with one space
    if (bytes + size > limit) {
      out.push(current);
      current = '';
      bytes = 0;
    }
    current += ch;
    bytes += size;
  }
  out.push(current);
  return out.join('\r\n ');
}

function nextDay(iso: string): string {
  const m = ISO_DAY.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1));
  return d.toISOString().slice(0, 10);
}

/** 16 hex digits of the conversation id (two FNV-1a passes): stable for one
 *  conversation, and the id itself — which opens the thread on the API — never
 *  lands in a file the visitor may forward. */
export function planKey(threadId: string): string {
  const fnv = (seed: number) => {
    let h = seed >>> 0;
    for (let i = 0; i < threadId.length; i += 1) {
      h ^= threadId.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  };
  return fnv(0x811c9dc5) + fnv(0x2f9a3b17);
}

function stamp(now: Date): string {
  return now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * The plan as an `.ics`, or `null` when the card carries no date.
 *
 * **One all-day event over the visit's days** when they run one after another —
 * one date, a weekend, a two-day trip: one UID for the conversation's plan
 * (`planKey`), so saving it again after the date moved, or after a weekend became
 * one day, UPDATES that entry instead of leaving an old day behind (Codex review
 * of !685 — a calendar never deletes an event a new file merely leaves out). A
 * two-day plan with its two dates heads each day's stops with its date; any other
 * combination lists the whole plan once — the dates are the visitor's, the split of
 * the stops between them is not ours to invent.
 *
 * Dates with a gap between them (rare: two named days apart) are one event each,
 * `-1`, `-2` — an event spanning the gap would put the visit on a day nobody named.
 *
 * **The way back into the guide** (*link*, INV-PH-166) is the event's `URL` and a line of its text — a calendar
 * app shows the first as a button, and a reader that drops it still has the second.
 */
export function planIcs(props: TripPlanProps, now: Date, threadId: string, link: string | null = null): string | null {
  const dates = (props.dates ?? []).filter((d) => ISO_DAY.test(d));
  if (dates.length === 0) return null;
  const doc = planDocument(props, now, link);
  const perDay = doc.days.length > 1 && doc.days.length === dates.length;
  const consecutive = dates.every((date, i) => i === 0 || nextDay(dates[i - 1]!) === date);

  const footer = [doc.asOf, CHECK_LINE].filter(Boolean).join('. ');
  const dayLines = (day: ExportDay, date?: string): string[] => [
    ...(day.heading ? [date ? `${day.heading} (${ruDate(date)})` : day.heading] : []),
    ...day.stops.map(stopLine),
  ];
  const describe = (body: string[]): string =>
    [`Дата: ${doc.date}`, doc.totals, ...body, link ? `${LINK_LINE}: ${link}` : '', footer].filter(Boolean).join('\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//peterhof.aiagentslab.ru//plan export//RU',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];
  const key = planKey(threadId);
  const event = (n: number, first: string, last: string, summary: string, body: string[]) =>
    lines.push(
      'BEGIN:VEVENT',
      `UID:peterhof-plan-${key}-${n}@peterhof.aiagentslab.ru`,
      `DTSTAMP:${stamp(now)}`,
      `DTSTART;VALUE=DATE:${first.replace(/-/g, '')}`,
      `DTEND;VALUE=DATE:${nextDay(last).replace(/-/g, '')}`,
      `SUMMARY:${icsText(summary)}`,
      `LOCATION:${icsText('Петергоф')}`,
      ...(link ? [`URL:${link}`] : []),
      `DESCRIPTION:${icsText(describe(body))}`,
      'TRANSP:TRANSPARENT',
      'END:VEVENT',
    );
  const summary = `Петергоф: ${doc.title}`;
  if (consecutive) {
    const body = perDay
      ? doc.days.flatMap((day, i) => dayLines(day, dates[i]))
      : doc.days.flatMap((day) => dayLines(day));
    event(1, dates[0]!, dates[dates.length - 1]!, summary, body);
  } else {
    dates.forEach((date, i) => {
      const day = perDay ? doc.days[i]! : null;
      event(
        i + 1,
        date,
        date,
        day?.heading ? `${summary} — ${day.heading}` : summary,
        day ? dayLines(day) : doc.days.flatMap((d) => dayLines(d)),
      );
    });
  }
  lines.push('END:VCALENDAR');
  return lines.map(foldLine).join('\r\n') + '\r\n';
}
